import React, { useMemo } from "react";

import { useSelector } from "react-redux";

import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { SecondaryMultiButton } from "components/Button/MultiButton";
import { Column } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";

import { RowClassParams } from "ag-grid-community";

import { AgGridTables } from "constants/aggrid";
import {
  BillingDocumentColId,
  BillingDocumentColumnDef,
  BusinessColId,
  BusinessColumnDef,
  LedgerEntryColId,
  LedgerEntryColumnDef,
} from "constants/columnDefinitions";
import { LiabilityAccountTypes } from "constants/ledgerAccounts";
import { ApiModel } from "constants/loading";

import { avgAggFunction } from "lib/agGrid/aggregators";

import {
  getActiveBillingRunId,
  getActiveRole,
  getBillingTags,
  getProperties,
} from "selectors";

import { selectCommittedLedgerEntryData } from "selectors/aggrid/ledgerEntries";
import { selectLedgerAccountByCodeLookup } from "selectors/ledgerAccounts";

const getRowId = (params: { data: { ledgerEntry: { id: string } } }) =>
  params.data.ledgerEntry.id;
const aggFuncs = { anAggFunc: avgAggFunction };

// Highlight grouped GL code rows where the account is a liability type and
// the credit and debit amounts don't balance.
const rowClassRules = {
  "row-warning": ({ node, context }: RowClassParams) => {
    if (!node.group) return false;
    if (node.level !== 0) return false;
    if (node.rowGroupColumn?.getColId() !== LedgerEntryColId.GL_CODE)
      return false;

    const ledgerAccount = context.ledgerAccountByCode[node.key];
    if (!ledgerAccount || !LiabilityAccountTypes.has(ledgerAccount.accountType))
      return false;

    const creditAmount = node.aggData?.[LedgerEntryColId.CREDIT_AMOUNT];
    const debitAmount = node.aggData?.[LedgerEntryColId.DEBIT_AMOUNT];

    return Math.round(creditAmount * 100) !== Math.round(debitAmount * 100);
  },
};

const columnDefs = [
  {
    headerName: "Document",
    children: Object.values(BillingDocumentColumnDef).filter(
      ({ colId }) => colId !== BillingDocumentColId.ACTIONS,
    ),
  },
  {
    headerName: "Business",
    children: Object.values(BusinessColumnDef).filter(
      ({ colId }) => colId !== BusinessColId.ACTIONS,
    ),
  },
  {
    headerName: "Ledger Entry",
    children: Object.values(LedgerEntryColumnDef),
  },
];

export const LedgerEntryDetail = () => {
  const allRowData = useSelector(selectCommittedLedgerEntryData);
  const billingRunId = useSelector(getActiveBillingRunId);

  const rowData = useMemo(
    () =>
      allRowData.filter(
        row => row.billingDocument?.billingRunId === billingRunId,
      ),
    [allRowData, billingRunId],
  );

  const billingTags = useSelector(getBillingTags);
  const userRoleSlug = useSelector(state => getActiveRole(state).slug);
  const propertyByIdLookup = useSelector(getProperties);
  const ledgerAccountByCode = useSelector(selectLedgerAccountByCodeLookup);
  const context = useMemo(
    () => ({
      billingTags,
      ledgerAccountByCode,
      propertyByIdLookup,
      userRoleSlug,
    }),
    [billingTags, ledgerAccountByCode, propertyByIdLookup, userRoleSlug],
  );

  return (
    <WaitForSync
      requiredData={[
        ApiModel.BILLING_DOCUMENTS,
        ApiModel.BILLING_RUNS,
        ApiModel.BUSINESSES,
        ApiModel.EMAIL,
        ApiModel.LEDGER_ENTRIES,
        ApiModel.MANUAL_ADJUSTMENTS,
      ]}
    >
      <Column fullHeight>
        <AgGridTable
          rowClassRules={rowClassRules}
          aggFuncs={aggFuncs}
          columnDefs={columnDefs}
          getRowId={getRowId}
          context={context}
          rowData={rowData}
          showGlobalSearchFilter={false}
          WrapperComponent={TableWrapper}
          tableName={AgGridTables.BILLING_LEDGER_ENTRIES_DETAIL}
          multiButtonComponent={SecondaryMultiButton}
          groupIncludeFooter
          groupIncludeTotalFooter
        />
      </Column>
    </WaitForSync>
  );
};
